import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  TextField,
  Typography,
  Step,
  StepLabel,
  Stepper,
  Card,
  CardContent,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const steps = [
  "Contact Info",
  "Address",
  "Usage & Agreement",
  "Login",
  "Options",
];

const LOGIN_REGEX = /^[a-zA-Z0-9._-]{3,50}$/;
const EMAIL_REGEX = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const PHONE_REGEX = /^[\d\s()+-]+$/;
const ZIP_REGEX = /^[0-9]{5}(-[0-9]{4})?$/;

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  "6LdmsQIrAAAAAFk90vfXAM38LpbvyJYsDO8eInG0";

const STEP_FIELDS = {
  0: ["name", "email", "company", "phoneNumber"],
  1: ["address.street", "address.city", "address.state", "address.zip"],
  2: ["intendedUsage", "intendedLocation"],
  3: ["login", "password"],
  4: [],
};

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [passStrength, setPassStrength] = useState();
  const [pwdSuggestion, setPwdSuggestion] = useState("");
  const [strength, setStrength] = useState({ label: "", score: 0 });
  const [passwordValid, setPasswordValid] = useState(false);
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    login: "",
    password: "",
    name: "",
    email: "",
    company: "",
    phoneNumber: "",
    faxNumber: "",
    address: {
      street: "",
      city: "",
      state: "",
      zip: "",
    },
    intendedUsage: "",
    intendedLocation: "",
    requestLink: false,
    requestXml: false,
    revenue: false,
  });

  const [errors, setErrors] = useState({});
  const [loginAvailable, setLoginAvailable] = useState(null);

  const loginCheckTimer = useRef(null);
  const passwordCheckTimer = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("address.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [key]: value,
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const checkLoginAvailability = async (login) => {
    if (!login || login.trim().length === 0) {
      setLoginAvailable(null);
      return;
    }

    try {
      const response = await fetch(
        process.env.REACT_APP_API_HOST +
          `/user/download/check-login.json?login=${encodeURIComponent(login)}&excludeCurrentUser=false`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setLoginAvailable(data.data.available);

      if (!data.data.available) {
        setErrors((prev) => ({
          ...prev,
          login: "Login name is already taken",
        }));
      } else {
        setErrors((prev) => {
          const { login, ...rest } = prev;
          return rest;
        });
      }
    } catch (err) {
      setLoginAvailable(null);
    }
  };

  useEffect(() => {
    if (loginCheckTimer.current) clearTimeout(loginCheckTimer.current);
    const login = form.login;
    if (!login) {
      setLoginAvailable(null);
      return;
    }
    if (!LOGIN_REGEX.test(login)) {
      setLoginAvailable(null);
      return;
    }
    loginCheckTimer.current = setTimeout(() => {
      checkLoginAvailability(login);
    }, 400);
    return () => {
      if (loginCheckTimer.current) clearTimeout(loginCheckTimer.current);
    };
  }, [form.login]);

  const updateStrength = (password) => {
    let score = 0;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    let label = "Weak";
    if (score >= 4) label = "Strong";
    else if (score === 2 || score === 3) label = "Medium";
    setStrength({ label, score });
  };

  const handlePassword = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    updateStrength(value);

    if (errors.password) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.password;
        return next;
      });
    }

    if (passwordCheckTimer.current) clearTimeout(passwordCheckTimer.current);

    if (!value) {
      setPasswordValid(false);
      setPwdSuggestion("");
      setPassStrength(undefined);
      return;
    }

    passwordCheckTimer.current = setTimeout(async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_API_HOST + "/user/check-password.json",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ password: value }),
          },
        );
        const json = await response.json();
        const data = json?.data;
        if (!data) {
          setPasswordValid(false);
          return;
        }
        setPassStrength(data.strength);
        setPasswordValid(!!data.valid);
        if (data.valid) {
          setPwdSuggestion(
            data.message || "Password meets all security requirements",
          );
        } else {
          const details = Array.isArray(data.details)
            ? data.details.join("; ")
            : data.details || "";
          setPwdSuggestion(
            details
              ? `${details}${data.message ? "; " + data.message : ""}`
              : data.message || "Password does not meet security requirements",
          );
        }
      } catch (err) {
        setPasswordValid(false);
      }
    }, 400);
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const stepIsComplete = (step) => {
    if (step === 0) {
      if (!form.name || !form.company) return false;
      if (!form.email || !EMAIL_REGEX.test(form.email)) return false;
      if (!form.phoneNumber || !PHONE_REGEX.test(form.phoneNumber))
        return false;
      if (form.faxNumber && !PHONE_REGEX.test(form.faxNumber)) return false;
      return true;
    }
    if (step === 1) {
      const a = form.address || {};
      if (!a.street || !a.city || !a.state) return false;
      if (!a.zip || !ZIP_REGEX.test(a.zip)) return false;
      return true;
    }
    if (step === 2) {
      return !!form.intendedUsage && !!form.intendedLocation;
    }
    if (step === 3) {
      if (!form.login || !LOGIN_REGEX.test(form.login)) return false;
      if (loginAvailable === false) return false;
      if (!passwordValid) return false;
      return true;
    }
    if (step === 4) {
      return true;
    }
    return false;
  };

  const derivedCompletedSteps = useMemo(
    () => steps.map((_, i) => stepIsComplete(i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, loginAvailable, passwordValid],
  );

  const validate = () => {
    const newErrors = {};

    if (!form.name) newErrors.name = "Required";
    if (!form.company) newErrors.company = "Required";
    if (!form.intendedUsage) newErrors.intendedUsage = "Required";
    if (!form.intendedLocation) newErrors.intendedLocation = "Required";

    if (!form.email) newErrors.email = "Required";
    else if (!EMAIL_REGEX.test(form.email)) newErrors.email = "Invalid email";

    if (!form.phoneNumber) newErrors.phoneNumber = "Required";
    else if (!PHONE_REGEX.test(form.phoneNumber))
      newErrors.phoneNumber = "Digits, spaces and ( ) + - only";

    if (form.faxNumber && !PHONE_REGEX.test(form.faxNumber)) {
      newErrors.faxNumber = "Digits, spaces and ( ) + - only";
    }

    if (!form.login) newErrors.login = "Required";
    else if (!LOGIN_REGEX.test(form.login)) {
      newErrors.login = "3�50 characters; letters, digits, . _ - only";
    } else if (loginAvailable === false) {
      newErrors.login = "Login name is already taken";
    }

    if (!form.password) newErrors.password = "Required";
    else if (!passwordValid) {
      newErrors.password =
        pwdSuggestion || "Password does not meet security requirements";
    }

    if (!form.address) {
      newErrors["address.street"] = "Required";
      newErrors["address.city"] = "Required";
      newErrors["address.state"] = "Required";
      newErrors["address.zip"] = "Required";
    } else {
      ["street", "city", "state"].forEach((key) => {
        if (!form.address[key]) newErrors[`address.${key}`] = "Required";
      });
      if (!form.address.zip) newErrors["address.zip"] = "Required";
      else if (!ZIP_REGEX.test(form.address.zip))
        newErrors["address.zip"] = "ZIP or ZIP+4";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const firstStepWithError = (errs) => {
    for (let i = 0; i < steps.length; i++) {
      const fields = STEP_FIELDS[i] || [];
      if (fields.some((f) => errs[f])) return i;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setServerError("");
    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      const step = firstStepWithError(clientErrors);
      if (step !== null) setActiveStep(step);
      return;
    }

    setSubmitting(true);
    try {
      if (!window.grecaptcha || !window.grecaptcha.execute) {
        setServerError(
          "reCAPTCHA is not available. Please reload the page and try again.",
        );
        return;
      }

      const recaptchaToken = await window.grecaptcha.execute(
        RECAPTCHA_SITE_KEY,
        {
          action: "register",
        },
      );

      const payload = {
        ...form,
        recaptchaResponse: recaptchaToken,
      };

      const res = await fetch(
        process.env.REACT_APP_API_HOST + "/user/register.json",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const result = await res.json().catch(() => null);

      if (res.ok && result && result.success) {
        alert(
          "Registration successful. You will be notified when your account is approved.",
        );
        const sameOriginReferrer =
          document.referrer &&
          document.referrer.startsWith(window.location.origin);
        if (sameOriginReferrer && window.history.length > 1) {
          navigate(-1);
        } else {
          navigate("/");
        }
        return;
      }

      const apiError = result && result.error;
      const fieldErrors = (apiError && apiError.fieldErrors) || {};
      const errorMessage =
        (apiError && apiError.message) ||
        `HTTP ${res.status} ${res.statusText}`;

      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        const step = firstStepWithError(fieldErrors);
        if (step !== null) setActiveStep(step);
        const captchaMsg = fieldErrors.captcha;
        if (captchaMsg) {
          setServerError(captchaMsg);
        } else {
          setServerError(
            "Please correct the highlighted fields and try again.",
          );
        }
      } else {
        setServerError(errorMessage);
      }
    } catch (err) {
      console.error("Registration error:", err);
      setServerError(`Registration failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getStrengthColor = () => {
    if (strength.label === "Weak") return "error";
    if (strength.label === "Medium") return "warning";
    if (strength.label === "Strong") return "success";
    return "inherit";
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const tag = e.target.tagName;
    const type = (e.target.type || "").toLowerCase();
    if (tag === "TEXTAREA") return;
    if (tag === "BUTTON" && type === "submit") return;
    e.preventDefault();
  };

  return (
    <Box
      sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 2 }}
    >
      <Card elevation={2} sx={{ maxWidth: 1100, width: "100%", p: 2 }}>
        <CardContent sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom align="center">
            Traffic Information Access/Reuse Registration
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ mb: 2 }}
            size="small"
          >
            {steps.map((label, index) => (
              <Step key={label} completed={derivedCompletedSteps[index]}>
                <StepLabel>
                  <Typography variant="caption">{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          {serverError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setServerError("")}
            >
              {serverError}
            </Alert>
          )}
          <form
            onSubmit={handleSubmit}
            onKeyDown={handleFormKeyDown}
            noValidate
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box onFocus={() => setActiveStep(0)} tabIndex={-1}>
                  <Card variant="outlined" sx={{ mb: 1, pb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Contact Information
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Company/Organization"
                            name="company"
                            value={form.company}
                            onChange={handleChange}
                            error={!!errors.company}
                            helperText={errors.company}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            name="phoneNumber"
                            value={form.phoneNumber}
                            onChange={handleChange}
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Fax Number"
                            name="faxNumber"
                            value={form.faxNumber}
                            onChange={handleChange}
                            error={!!errors.faxNumber}
                            helperText={errors.faxNumber}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
                <Box onFocus={() => setActiveStep(1)} tabIndex={-1}>
                  <Card variant="outlined" sx={{ pb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Address
                      </Typography>
                      <Grid container spacing={1}>
                        {["street", "city", "state", "zip"].map((field) => (
                          <Grid item xs={12} sm={6} key={field}>
                            <TextField
                              fullWidth
                              label={
                                field.charAt(0).toUpperCase() + field.slice(1)
                              }
                              name={`address.${field}`}
                              value={form.address[field]}
                              onChange={handleChange}
                              error={!!errors[`address.${field}`]}
                              helperText={errors[`address.${field}`]}
                              size="small"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box onFocus={() => setActiveStep(2)} tabIndex={-1}>
                  <Card variant="outlined" sx={{ mb: 1, pb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Usage & Agreement
                      </Typography>
                      <Typography fontSize={12} sx={{ mb: 1 }}>
                        Users must use this form to access traffic information
                        from the Illinois Department of Transportation (IDOT),
                        Gateway Traveler Information System (Gateway) for
                        purposes such as republication or redistribution,
                        inputting components of traffic information from other
                        sources, or establishment of a link to this IDOT
                        website.
                      </Typography>
                      <TextField
                        fullWidth
                        label="Intended Usage"
                        name="intendedUsage"
                        value={form.intendedUsage}
                        onChange={handleChange}
                        error={!!errors.intendedUsage}
                        helperText={errors.intendedUsage}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                      <TextField
                        fullWidth
                        label="Intended Location"
                        name="intendedLocation"
                        value={form.intendedLocation}
                        onChange={handleChange}
                        error={!!errors.intendedLocation}
                        helperText={errors.intendedLocation}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                </Box>
                <Box onFocus={() => setActiveStep(3)} tabIndex={-1}>
                  <Card variant="outlined" sx={{ mb: 1, pb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Login Information
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Login"
                            name="login"
                            value={form.login}
                            onChange={handleChange}
                            error={!!errors.login}
                            helperText={
                              errors.login ||
                              (loginAvailable === true
                                ? "Login name is available"
                                : loginAvailable === false
                                  ? "Login name is already taken"
                                  : "")
                            }
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type="password"
                            value={form.password}
                            onChange={handlePassword}
                            error={!!errors.password}
                            helperText={errors.password || pwdSuggestion}
                            size="small"
                          />
                          {passStrength && (
                            <Box sx={{ mt: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={(strength.score / 4) * 100}
                                color={getStrengthColor()}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                              <Typography fontSize={11} sx={{ mt: 0.5 }}>
                                {strength.label}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
                <Box onFocus={() => setActiveStep(4)} tabIndex={-1}>
                  <Card variant="outlined" sx={{ pb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="requestLink"
                                checked={form.requestLink}
                                onChange={handleCheckbox}
                                size="small"
                              />
                            }
                            label={
                              <Typography fontSize={12}>
                                Request Hyperlink
                              </Typography>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="requestXml"
                                checked={form.requestXml}
                                onChange={handleCheckbox}
                                size="small"
                              />
                            }
                            label={
                              <Typography fontSize={12}>Request XML</Typography>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="revenue"
                                checked={form.revenue}
                                onChange={handleCheckbox}
                                size="small"
                              />
                            }
                            label={
                              <Typography fontSize={12}>
                                Will earn revenue
                              </Typography>
                            }
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box textAlign="center" sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="medium"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting�" : "Register"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

const module = {
  routeProps: {
    path: "RegistrationForm",
    component: RegistrationForm,
  },

  name: "Registration Form",
  label: "Registration Form",
  title: "Traffic Information Access/Reuse Registration",
  showOnMenu: true,
};

export default module;
